import React, { useCallback, useEffect, useState } from "react";

import { apiGet, apiPost, getApiSettings, saveApiSettings } from "../../Api/Client.js";
import { Badge, Button, Card, Page, PageHeader } from "../../Ui/index.jsx";
import { EndpointUsageView, ModuleComparisonView } from "../admin/AdminPanelScreen.jsx";

// The operator console (Entry 20, design A), at /platform/. What the platform's own staff see:
// every company at a high level (plan, people count, products on, who its admins are), the plans
// and their prices, setting up a new company, and entering one company for support. Never a
// company's business records: to look inside a company, open a support session (recorded in
// that company's trail) and use the company's own screens.

const money = (p) => (p.pricePerPersonMonth ? `${p.currency || ""} ${p.pricePerPersonMonth} per person a month` : "Price not set");

export function OperatorConsoleScreen({ data, reload }) {
  const [companies, setCompanies] = useState(null);
  const [plans, setPlans] = useState([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", slug: "", adminName: "", adminUsername: "", adminEmail: "", plan: "everything" });
  const [operators, setOperators] = useState(null);
  const [opForm, setOpForm] = useState({ username: "", password: "" });
  const [pane, setPane] = useState("companies");
  const [endpointUsage, setEndpointUsage] = useState(null);
  const [endpointDays, setEndpointDays] = useState(14);

  const load = useCallback(() => {
    Promise.all([apiGet("/EnterpriseCore/platform/companies/"), apiGet("/EnterpriseCore/platform/plans/")])
      .then(([c, p]) => { setCompanies(c.companies || []); setPlans(p.plans || []); setError(""); })
      .catch((e) => setError(e?.payload?.message || e?.payload?.detail || e?.message || "Could not load companies."));
  }, []);
  useEffect(() => { load(); }, [load]);
  const loadOperators = useCallback(() => {
    apiGet("/Users/admin-panel/operator-logins/").then((r) => setOperators(r.operators || [])).catch(() => {});
  }, []);
  useEffect(() => { loadOperators(); }, [loadOperators]);
  useEffect(() => {
    if (pane !== "usage") return;
    apiGet(`/Users/admin-panel/endpoint-usage/?days=${endpointDays}`).then(setEndpointUsage).catch(() => {});
  }, [pane, endpointDays]);

  const createOperator = (e) => {
    e.preventDefault();
    run("operator", () => apiPost("/Users/admin-panel/operator-logins/", opForm), (res) => {
      setNotice(`Operator login ${res.created} is ready. Sign in with that username and its own password. Then remove operator access from the company login.`);
      setOpForm({ username: "", password: "" });
      loadOperators();
    });
  };
  const removeOperator = (username) => {
    if (!window.confirm(`Remove operator access from ${username}? Their company login, if they have one, stays.`)) return;
    run("operator", () => apiPost("/Users/admin-panel/operator-logins/", { username, remove: true }), () => loadOperators());
  };

  const run = async (key, fn, done) => {
    setBusy(key); setError(""); setNotice("");
    try { const res = await fn(); if (done) done(res); load(); }
    catch (e) { setError(e?.payload?.message || e?.payload?.detail || e?.message || "That was not done."); }
    finally { setBusy(""); }
  };

  const changePlan = (c, code) => {
    if (!code || code === c.plan?.code) return;
    if (!window.confirm(`Move ${c.name} to ${code}? Products the new plan leaves out are switched off; their data is kept.`)) return;
    run(`plan-${c.id}`, () => apiPost(`/EnterpriseCore/platform/companies/${c.id}/plan/`, { plan: code }),
      (res) => setNotice(`${c.name} is on ${res.plan}.` + (res.switchedOff?.length ? ` Switched off: ${res.switchedOff.join(", ")}.` : "")
        + (res.keptOnBecauseNeeded?.length ? ` Kept on because another product needs it: ${res.keptOnBecauseNeeded.join(", ")}.` : "")));
  };

  const nameAdmin = (c) => {
    const username = window.prompt(`Username of the person in ${c.name} who should be its admin:`);
    if (!username) return;
    run(`admin-${c.id}`, () => apiPost(`/EnterpriseCore/platform/companies/${c.id}/admins/`, { username: username.trim() }),
      () => setNotice(`${username} is now an admin of ${c.name}.`));
  };

  const enter = (c) => {
    if (!c.isYours && c.status !== "Active") return;
    if (c.isYours) { saveApiSettings({ ...getApiSettings(), tenantId: String(c.id) }); window.location.assign("/company-admin/"); return; }
    const reason = window.prompt(`Why do you need to enter ${c.name}? This is recorded in ${c.name}'s own trail.`);
    if (!reason) return;
    run(`enter-${c.id}`, () => apiPost(`/EnterpriseCore/platform/companies/${c.id}/support/`, { reason, minutes: 60 }), () => {
      saveApiSettings({ ...getApiSettings(), tenantId: String(c.id) });
      window.location.assign("/company-admin/");
    });
  };

  const create = (e) => {
    e.preventDefault();
    run("create", () => apiPost("/EnterpriseCore/platform/companies/", form), (res) => {
      setShowNew(false);
      setNotice(res.created
        ? `Company set up. Give its admin (${form.adminUsername}) this one-time password, privately: ${res.temporaryPassword || "(the one you set)"}`
        : "A company with that short name already exists; nothing was changed.");
      setForm({ name: "", slug: "", adminName: "", adminUsername: "", adminEmail: "", plan: "everything" });
    });
  };

  const field = (key, label, type = "text") => (
    <label className="ds-field" style={{ display: "block", marginBottom: 8 }}>
      <span className="ds-field__label">{label}</span>
      <input className="ds-input" type={type} required value={form[key]} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} />
    </label>
  );

  return (
    <Page>
      <PageHeader
        eyebrow="Platform"
        title="Companies"
        subtitle="Every company on the platform, at a high level. To look inside one, enter it for support; that is recorded in the company's own trail."
        actions={<Button variant="primary" onClick={() => setShowNew((v) => !v)}>{showNew ? "Cancel" : "New company"}</Button>}
      />
      <div className="ds-toolbar" style={{ gap: 8, marginBottom: 12 }}>
        <Button size="sm" variant={pane === "companies" ? "primary" : "ghost"} onClick={() => setPane("companies")}>Companies</Button>
        <Button size="sm" variant={pane === "usage" ? "primary" : "ghost"} onClick={() => setPane("usage")}>Endpoint usage</Button>
        <Button size="sm" variant={pane === "industry" ? "primary" : "ghost"} onClick={() => setPane("industry")}>Industry comparison</Button>
      </div>
      {pane === "usage" && <EndpointUsageView usage={endpointUsage} days={endpointDays} setDays={setEndpointDays} />}
      {pane === "industry" && <ModuleComparisonView />}
      {pane === "companies" && <>
      {error && <p className="ds-field__error">{error}</p>}
      {notice && <p className="ds-muted" style={{ background: "var(--c-50, #f6f6f6)", padding: 10, borderRadius: 6 }}>{notice}</p>}

      {showNew && (
        <Card title="Set up a new company" subtitle="Creates the company, its admin's login and the products its plan includes.">
          <form onSubmit={create}>
            {field("name", "Company name")}
            {field("slug", "Short name (letters, numbers, dashes)")}
            {field("adminName", "Admin's full name")}
            {field("adminUsername", "Admin's username")}
            {field("adminEmail", "Admin's email", "email")}
            <label className="ds-field" style={{ display: "block", marginBottom: 12 }}>
              <span className="ds-field__label">Plan</span>
              <select className="ds-input" value={form.plan} onChange={(e) => setForm((f) => ({ ...f, plan: e.target.value }))}>
                {plans.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
              </select>
            </label>
            <Button type="submit" variant="primary" disabled={busy === "create"}>{busy === "create" ? "Setting up…" : "Set up company"}</Button>
          </form>
        </Card>
      )}

      <Card title={companies ? `${companies.length} companies` : "Companies"}>
        {!companies && !error && <p className="ds-muted">Loading…</p>}
        {(companies || []).map((c) => (
          <div key={c.id} className="ds-toolbar" style={{ justifyContent: "space-between", alignItems: "flex-start", padding: "12px 0", borderTop: "1px solid var(--c-100, #eee)", flexWrap: "wrap", gap: 12 }}>
            <div style={{ minWidth: 220, flex: 1 }}>
              <div style={{ fontWeight: 600 }}>
                {c.name} {c.isYours && <Badge variant="info">Yours</Badge>} {c.status !== "Active" && <Badge variant="warning">{c.status}</Badge>}
                {c.support && <Badge variant="warning">You are in support until {new Date(c.support.endsAt).toLocaleTimeString()}</Badge>}
              </div>
              <div className="ds-card__sub">
                {c.people} people · {c.modulesOn} products and add-ons on ·
                Admins: {c.admins.length ? c.admins.map((a) => a.name || a.username).join(", ") : <strong> none named</strong>}
              </div>
            </div>
            <div className="ds-toolbar" style={{ gap: 8, flexWrap: "wrap" }}>
              <select className="ds-input" value={c.plan?.code || ""} disabled={busy === `plan-${c.id}`} onChange={(e) => changePlan(c, e.target.value)} title="Plan">
                {!c.plan && <option value="">No plan</option>}
                {plans.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
              </select>
              <Button size="sm" variant="ghost" disabled={busy === `admin-${c.id}`} onClick={() => nameAdmin(c)}>Name an admin</Button>
              {/* Entry 20 review: the server binds requests only to an Active company, so a support
                  session in a suspended or archived one would show its name over the operator's own data. */}
              <Button size="sm" variant={c.isYours ? "primary" : "ghost"}
                disabled={busy === `enter-${c.id}` || (!c.isYours && c.status !== "Active")}
                title={!c.isYours && c.status !== "Active" ? `${c.name} is ${String(c.status).toLowerCase()}; it cannot be entered.` : undefined}
                onClick={() => enter(c)}>
                {c.isYours ? "Open admin panel" : "Enter for support"}
              </Button>
            </div>
          </div>
        ))}
      </Card>

      <Card title="Operator login" subtitle="A different username and password from any company admin, including yours. This login is not an employee of a company.">
        <form onSubmit={createOperator} className="ds-toolbar" style={{ gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
          <input className="ds-input" placeholder="Username" value={opForm.username} onChange={(e) => setOpForm((f) => ({ ...f, username: e.target.value }))} />
          <input className="ds-input" type="password" placeholder="Password, 8 or more characters" value={opForm.password} onChange={(e) => setOpForm((f) => ({ ...f, password: e.target.value }))} />
          <Button type="submit" size="sm" variant="primary" disabled={busy === "operator"}>Create operator login</Button>
        </form>
        {(operators || []).map((op) => (
          <div key={op.id} className="ds-toolbar" style={{ justifyContent: "space-between", padding: "6px 0" }}>
            <span>{op.username}{op.isYou ? " · this login" : ""}{op.hasEmployeeRecord ? " · also a company employee" : ""}</span>
            {!op.isYou && <Button size="sm" variant="ghost" onClick={() => removeOperator(op.username)}>Remove operator access</Button>}
          </div>
        ))}
      </Card>

      <Card title="Plans and prices" subtitle="What a company can be on. A company's admin never sees this page.">
        {plans.map((p) => (
          <div key={p.code} style={{ padding: "8px 0", borderTop: "1px solid var(--c-100, #eee)" }}>
            <div style={{ fontWeight: 600 }}>{p.name} <span className="ds-muted" style={{ fontWeight: 400 }}>{p.code}{p.custom ? " · custom" : ""}</span></div>
            <div className="ds-card__sub">{money(p)} · Includes: {(p.includes || []).includes("*") ? "everything available" : (p.includes || []).join(", ") || "nothing yet"}</div>
          </div>
        ))}
      </Card>

      <Card title="File storage" subtitle="Every company's files are kept in the shared platform storage: private, with one folder per company. A company cannot bring its own storage yet." />
      </>}
    </Page>
  );
}

export default OperatorConsoleScreen;
